"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DatasheetActions = void 0;
const compensator_1 = require("../compensator");
const engine_1 = require("../engine");
const lodash_1 = require("lodash");
const utils_1 = require("../model/utils");
const store_1 = require("../exports/store");
const selectors_1 = require("../exports/store/selectors");
const types_1 = require("../types");
const field_types_1 = require("../types/field_types");
const utils_2 = require("../utils");
const field_1 = require("./field");
const views_1 = require("./views");
const view_derivate_1 = require("../compute_manager/view_derivate");
// TODO: all fields should be checked, not only the first one
function validateFilterInfo(filterInfo) {
    if (filterInfo == null) {
        return true;
    }
    if (!Array.isArray(filterInfo.conditions)) {
        return false;
    }
    if (filterInfo.conjunction == null) {
        return false;
    }
    return true;
}
function getDefaultNewRecordDataByGroup(groupInfos, groupCellValues) {
    const recordData = {};
    if (groupInfos.length === 0 || !groupCellValues) {
        return recordData;
    }
    groupInfos.forEach((group, i) => {
        recordData[group.fieldId] = groupCellValues[i];
    });
    return recordData;
}
function getDefaultNewRecordDataByFilter(state, datasheetId, filterInfo, fieldMap, userInfo) {
    const { conditions } = filterInfo;
    const recordData = {};
    // Or combination, there are multiple filter conditions, no default data fill in,
    // and there is only one filter condition, then it is processed according to the `and` logic
    if (filterInfo.conjunction === types_1.FilterConjunction.Or && filterInfo.conditions.length !== 1) {
        return recordData;
    }
    // And combination
    // 1. Group filter conditions by fieldId
    const conditionGroups = conditions.reduce((prev, condition) => {
        const { fieldId } = condition;
        let group = prev[fieldId];
        if (!group) {
            group = prev[fieldId] = [];
        }
        group.push(condition);
        return prev;
    }, {});
    // 2. Determine the final `And` fill value, for each field corresponding to the filter condition
    for (const fieldId in conditionGroups) {
        const field = fieldMap[fieldId];
        if (!field) {
            continue;
        }
        const isComputedField = field_1.Field.bindContext(field, state).isComputed;
        if (isComputedField) {
            // the cell of calc field, no need to fill in default value
            continue;
        }
        const conditionGroup = conditionGroups[fieldId];
        const isMultiValueField = field_1.Field.bindContext(field, state).isMultiValueField();
        const candidate = new Set();
        // 2.1. Collect all candidate values of all conditions
        for (let i = 0, ii = conditionGroup.length; i < ii; i++) {
            const condition = conditionGroup[i];
            const currentValue = field_1.Field.bindContext(field, state).defaultValueForCondition(condition);
            if (currentValue == null) {
                continue;
            }
            // multi-value type field, need to split the value "unique"
            // TODO: currently multi-value type field is basic type, so use "Set".
            // In the future, if reference type, optimize this
            if (isMultiValueField && Array.isArray(currentValue)) {
                currentValue.forEach(v => {
                    if (field.type === field_types_1.FieldType.Member && v === field_1.OtherTypeUnitId.Self) {
                        userInfo && candidate.add(userInfo.unitId);
                        return;
                    }
                    candidate.add(v);
                });
            }
            else {
                candidate.add(currentValue);
            }
        }
        // no candidate values
        if (candidate.size === 0) {
            continue;
        }
        let result;
        if (isMultiValueField) {
            // multi value field, can put all candidate values together
            result = [...candidate];
        }
        else if (candidate.size === 1) {
            // single value field, only one value can meet the `And` condition
            result = candidate.values().next().value;
        }
        else {
            // single value field, more than one value can meet the `And` condition, which is not allowed
            continue;
        }
        /**
         * candidate default value must be able to pass all filter conditions.
         * assume that there are two filter conditions for the numeric field, =1 And < 0.
         * candidate value logic is 1, calc by the above.
         * and 1 cannot pass =1 and <0, default fill in this value does not meet user expectations.
         */
        const viewFilterDerivate = new view_derivate_1.ViewFilterDerivate(state, datasheetId);
        const pass = conditionGroup.every(condition => viewFilterDerivate.doFilter(condition, field, result));
        if (pass) {
            // different fields of `And`, only need to assign the values that have values to them.
            recordData[fieldId] = result;
        }
    }
    return recordData;
}
class DatasheetActions {
    /**
     * add `view` to table
     */
    static addView2Action(snapshot, payload) {
        const { view } = payload;
        let { startIndex } = payload;
        const views = snapshot.meta.views;
        if (views.length >= (0, utils_1.getMaxViewCountPerSheet)()) {
            return null;
        }
        if (!views.every(viw => viw.id !== view.id)) {
            return null;
        }
        if (!(startIndex !== undefined && startIndex >= 0 && startIndex <= views.length)) {
            startIndex = views.length;
        }
        // when add new datasheet view, set default summary value of the analysis bar
        const setDefaultFieldStat = (view) => {
            if (view.type !== store_1.ViewType.Grid)
                return view;
            const newView = Object.assign(Object.assign({}, view), { columns: view.columns.map((col, i) => {
                    var _a, _b;
                    if (i < 1)
                        return Object.assign(Object.assign({}, col), { statType: field_1.StatType.CountAll });
                    if ([field_types_1.FieldType.Number, field_types_1.FieldType.Currency].includes((_b = (_a = snapshot.meta.fieldMap) === null || _a === void 0 ? void 0 : _a[col.fieldId]) === null || _b === void 0 ? void 0 : _b.type)) {
                        return Object.assign(Object.assign({}, col), { statType: field_1.StatType.Sum });
                    }
                    return col;
                }) });
            return newView;
        };
        return {
            n: engine_1.OTActionName.ListInsert,
            p: ['meta', 'views', startIndex],
            li: setDefaultFieldStat(view),
        };
    }
    /**
     * delete view based viewID,
     *
     * @param {string} viewId
     */
    static deleteView2Action(snapshot, payload) {
        const views = snapshot.meta.views;
        const viewId = payload.viewId;
        // check whether current is activeView
        const viewIndex = (0, lodash_1.findIndex)(views, viw => viw.id === viewId);
        if (viewIndex < 0) {
            return null;
        }
        return {
            n: engine_1.OTActionName.ListDelete,
            p: ['meta', 'views', viewIndex],
            ld: views[viewIndex],
        };
    }
    /**
     * update view based viewID
     *
     * @param {string} viewId
     */
    static modifyView2Action(snapshot, payload) {
        const views = snapshot.meta.views;
        const { viewId, key, value } = payload;
        // check whether current is activeView
        const viewIndex = (0, lodash_1.findIndex)(views, viw => viw.id === viewId);
        if (viewIndex < 0) {
            return null;
        }
        if (key === 'columns' && Array.isArray(value)) {
            const rlt = [];
            value.forEach(item => {
                const fieldId = item.fieldId;
                const view = views[viewIndex];
                const columns = view['columns'];
                const modifyColumnIndex = columns.findIndex(column => column.fieldId === fieldId);
                const oldItem = columns[modifyColumnIndex];
                if (!(0, lodash_1.isEqual)(oldItem, item)) {
                    rlt.push({
                        n: engine_1.OTActionName.ListReplace,
                        p: ['meta', 'views', viewIndex, 'columns', modifyColumnIndex],
                        ld: oldItem,
                        li: item,
                    });
                }
            });
            return rlt;
        }
        return [
            {
                n: engine_1.OTActionName.ObjectReplace,
                p: ['meta', 'views', viewIndex, key],
                od: views[viewIndex][key],
                oi: value,
            },
        ];
    }
    /**
     * add Field to table
     */
    static addField2Action(snapshot, payload) {
        const fieldMap = snapshot.meta.fieldMap;
        const views = snapshot.meta.views;
        const { field, viewId, fieldId, offset, index, hiddenColumn } = payload;
        const actions = views.reduce((pre, cur, viewIndex) => {
            let columnIndex = cur.columns.length;
            if (cur.columns.some(column => column.fieldId === field.id)) {
                return pre;
            }
            // as new columns with duplicate operation, pass in `index` to take precedence
            if (viewId && index && viewId === cur.id) {
                columnIndex = index;
            }
            // only under specified view, if fieldId exists, calc index by every view's fieldId
            if (fieldId) {
                const fieldIdIndex = cur.columns.findIndex(column => column.fieldId === fieldId);
                columnIndex = fieldIdIndex + (offset !== null && offset !== void 0 ? offset : 0);
            }
            const newColumn = {
                fieldId: field.id,
            };
            if ([field_types_1.FieldType.Number, field_types_1.FieldType.Currency].includes(field.type)) {
                newColumn.statType = field_1.StatType.Sum;
            }
            // handler of new column in view
            function viewColumnHandler() {
                let hiddenKey = 'hidden';
                switch (cur.type) {
                    case store_1.ViewType.Gantt:
                        hiddenKey = 'hiddenInGantt';
                        break;
                    case store_1.ViewType.Calendar:
                        hiddenKey = 'hiddenInCalendar';
                        break;
                    case store_1.ViewType.OrgChart:
                        hiddenKey = 'hiddenInOrgChart';
                        break;
                    default:
                        // current view default display or view has no hidden column
                        if ((viewId && viewId === cur.id) || cur.columns.every(column => !column.hidden)) {
                            newColumn.hidden = Boolean(hiddenColumn);
                            return;
                        }
                        break;
                }
                const isGanttView = viewId === cur.id && cur.type === store_1.ViewType.Gantt;
                newColumn.hidden = !isGanttView || hiddenColumn;
                newColumn[hiddenKey] = true;
            }
            viewColumnHandler();
            pre.push({
                n: engine_1.OTActionName.ListInsert,
                p: ['meta', 'views', viewIndex, 'columns', columnIndex],
                li: newColumn,
            });
            return pre;
        }, []);
        if (!fieldMap[field.id]) {
            actions.push({
                n: engine_1.OTActionName.ObjectInsert,
                p: ['meta', 'fieldMap', field.id],
                oi: field,
            });
        }
        return actions;
    }
    /**
     * delete field
     */
    static deleteField2Action(snapshot, payload) {
        const fieldMap = snapshot.meta.fieldMap;
        const views = snapshot.meta.views;
        const { fieldId, datasheetId, viewId } = payload;
        // delete all columns related attributes in all view
        const actions = views.reduce((action, view, index) => {
            const columnIndex = view.columns.findIndex(column => column.fieldId === fieldId);
            if (columnIndex < 0) {
                return action;
            }
            const deleteGroupOrSortInfo = (type) => {
                var _a;
                const info = type === 'groupInfo' ? view.groupInfo : (_a = view.sortInfo) === null || _a === void 0 ? void 0 : _a.rules;
                if (info) {
                    const infoIndex = info.findIndex(gp => gp.fieldId === fieldId);
                    if (infoIndex > -1 && info.length > 1) {
                        if (type === 'groupInfo') {
                            action.push({
                                n: engine_1.OTActionName.ListDelete,
                                p: ['meta', 'views', index, type, infoIndex],
                                ld: info[infoIndex],
                            });
                            compensator_1.compensator.setLastGroupInfoIfNull(info);
                        }
                        else {
                            action.push({
                                n: engine_1.OTActionName.ListDelete,
                                p: ['meta', 'views', index, type, 'rules', infoIndex],
                                ld: info[infoIndex],
                            });
                        }
                    }
                    // remain the last one, need to delete the group/sort field directly
                    if (infoIndex > -1 && info.length === 1) {
                        action.push({
                            n: engine_1.OTActionName.ObjectDelete,
                            p: ['meta', 'views', index, type],
                            od: type === 'groupInfo' ? info : view.sortInfo,
                        });
                    }
                }
            };
            const deleteKanbanFieldId = () => {
                const isKanbanView = view.type === store_1.ViewType.Kanban && view.style.kanbanFieldId === fieldId;
                if (isKanbanView) {
                    action.push({
                        n: engine_1.OTActionName.ObjectDelete,
                        p: ['meta', 'views', index, 'style', 'kanbanFieldId'],
                        od: fieldId,
                    });
                }
            };
            const deleteGanttFieldId = () => {
                if (view.type !== store_1.ViewType.Gantt) {
                    return;
                }
                const isStartField = view.style.startFieldId === fieldId;
                const isEndField = view.style.endFieldId === fieldId;
                if (isStartField) {
                    action.push({
                        n: engine_1.OTActionName.ObjectDelete,
                        p: ['meta', 'views', index, 'style', 'startFieldId'],
                        od: fieldId,
                    });
                }
                if (isEndField) {
                    action.push({
                        n: engine_1.OTActionName.ObjectDelete,
                        p: ['meta', 'views', index, 'style', 'endFieldId'],
                        od: fieldId,
                    });
                }
            };
            const setFrozenColumnCount = () => {
                const frozenColumnCount = view === null || view === void 0 ? void 0 : view.frozenColumnCount;
                if (frozenColumnCount && columnIndex < frozenColumnCount) {
                    action.push({
                        n: engine_1.OTActionName.ObjectReplace,
                        p: ['meta', 'views', index, 'frozenColumnCount'],
                        oi: frozenColumnCount - 1,
                        od: frozenColumnCount,
                    });
                }
            };
            if (datasheetId !== snapshot.datasheetId || !(view.lockInfo && view.id !== viewId)) {
                // judgement here is for the permissions tips of lock view.
                // for example, `view 2` set field A's filter condition, I delete field A in `view 1`,
                // because `view 2`'s view lock will make me operation failed,
                // from user's point of view, he may not delete one field before check all views, and close view lock
                // so, judgement here is to delete field, and at the same time, doesn't delete the information in lock view,
                // just show exception tips only
                // what's special, is the relation table operation, verifications of relation table in middle server(room-server) is not strict,
                // only require editable permission, therefor, relation table operation can go pass directly.
                // the dependencies in filter's field, also need to be deleted
                if (view.filterInfo) {
                    const newConditions = view.filterInfo.conditions.filter(condition => {
                        if (condition.fieldId === fieldId) {
                            return false;
                        }
                        return true;
                    });
                    if (newConditions.length === 0) {
                        action.push({
                            n: engine_1.OTActionName.ObjectDelete,
                            p: ['meta', 'views', index, 'filterInfo'],
                            od: view.filterInfo,
                        });
                    }
                    else if (newConditions.length !== view.filterInfo.conditions.length) {
                        action.push({
                            n: engine_1.OTActionName.ObjectReplace,
                            p: ['meta', 'views', index, 'filterInfo', 'conditions'],
                            od: view.filterInfo.conditions,
                            oi: newConditions,
                        });
                    }
                }
                deleteGroupOrSortInfo('groupInfo');
                deleteGroupOrSortInfo('sortInfo');
                deleteKanbanFieldId();
                deleteGanttFieldId();
                setFrozenColumnCount();
            }
            // delete columns
            action.push({
                n: engine_1.OTActionName.ListDelete,
                p: ['meta', 'views', index, 'columns', columnIndex],
                ld: view.columns[columnIndex],
            });
            return action;
        }, []);
        const field = fieldMap[fieldId];
        if (field) {
            // when delete date column, remove alarm
            const recordMap = snapshot.recordMap;
            Object.keys(recordMap).forEach(recordId => {
                const alarm = store_1.Selectors.getDateTimeCellAlarm(snapshot, recordId, field.id);
                if (alarm) {
                    const alarmActions = DatasheetActions.setDateTimeCellAlarm(snapshot, {
                        recordId,
                        fieldId: field.id,
                        alarm: null,
                    });
                    if (alarmActions) {
                        actions.push(...alarmActions);
                    }
                }
            });
            actions.push({
                n: engine_1.OTActionName.ObjectDelete,
                p: ['meta', 'fieldMap', fieldId],
                od: field,
            });
        }
        return actions;
    }
    /**
     * add record to table
     */
    static addRecord2Action(snapshot, payload) {
        const recordMap = snapshot.recordMap;
        const views = snapshot.meta.views;
        const { record, index, viewId } = payload;
        const actions = views.reduce((pre, cur, viewIndex) => {
            let rowIndex = cur.rows.length;
            if (cur.rows.some(row => row.recordId === record.id)) {
                return pre;
            }
            // only add to index when view is active
            if (viewId === cur.id) {
                rowIndex = index;
            }
            pre.push({
                n: engine_1.OTActionName.ListInsert,
                p: ['meta', 'views', viewIndex, 'rows', rowIndex],
                li: { recordId: record.id },
            });
            return pre;
        }, []);
        if (!recordMap[record.id]) {
            actions.push({
                n: engine_1.OTActionName.ObjectInsert,
                p: ['recordMap', record.id],
                oi: record,
            });
        }
        return actions;
    }
    /**
     * update record, when return null, it means record does not exist and current table's value has not changed
     */
    static setRecord2Action(snapshot, payload) {
        const { recordId, fieldId, value } = payload;
        // recordId, not exist in data
        if (!snapshot.recordMap[recordId]) {
            return null;
        }
        // oldCellValue, don't use `getCellValue`, because it may be a computed field
        const cv = snapshot.recordMap[recordId].data[fieldId];
        const oldCellValue = cv == null ? null : cv;
        // non-computed field, check whether value has changed
        if ((0, lodash_1.isEqual)(oldCellValue, value)) {
            return null;
        }
        // when value is empty(empty array), we should delete the key to avoid redundancy
        if (value == null || (Array.isArray(value) && value.length === 0)) {
            return {
                n: engine_1.OTActionName.ObjectDelete,
                p: ['recordMap', recordId, 'data', fieldId],
                od: oldCellValue,
            };
        }
        // when origin cellValue is empty, in fact it need insert one fieldId key
        if (oldCellValue == null) {
            return {
                n: engine_1.OTActionName.ObjectInsert,
                p: ['recordMap', recordId, 'data', fieldId],
                oi: value,
            };
        }
        return {
            n: engine_1.OTActionName.ObjectReplace,
            p: ['recordMap', recordId, 'data', fieldId],
            od: oldCellValue,
            oi: value,
        };
    }
    /**
     * delete record by record id
     */
    static deleteRecords(snapshot, payload) {
        const recordMap = snapshot.recordMap;
        const recordSize = Object.keys(recordMap).length;
        const views = snapshot.meta.views;
        const fieldMap = snapshot.meta.fieldMap;
        const { recordIds, getFieldByFieldId, state } = payload;
        const waitDeleteRecordSet = new Set(recordIds);
        compensator_1.compensator.addWillRemoveRecords(recordIds);
        let actions = [];
        /**
         *
         * depends on records count to delete,  and the percent of delete count and total count to judge
         * delete one by one (ld) ,  or total replace(or)
         *
         * when the number of records to delete larger than 500,
         * or delete records / total records percent larger thant 50%,
         * and total records larger than 160,
         * then total replace
         * otherwise delete one by one
         *
         */
        // delete all rows in views
        const rate = waitDeleteRecordSet.size / recordSize;
        if ((rate > 0.5 && recordSize > 100) || waitDeleteRecordSet.size > 500) {
            // delete related records in views
            actions = views.map((curView, index) => {
                const nextViewRows = curView.rows.reduce((pre, row) => {
                    if (!waitDeleteRecordSet.has(row.recordId)) {
                        pre.push(row);
                    }
                    return pre;
                }, []);
                return {
                    n: engine_1.OTActionName.ObjectReplace,
                    p: ['meta', 'views', index, 'rows'],
                    od: curView.rows,
                    oi: nextViewRows,
                };
            });
        }
        else {
            // delete one by one
            actions = views.reduce((pre, cur, index) => {
                const toDelete = cur.rows
                    .reduce((pre, row, i) => {
                    if (waitDeleteRecordSet.has(row.recordId)) {
                        pre.push({
                            recordId: row.recordId,
                            index: i,
                        });
                    }
                    return pre;
                }, [])
                    .map((item, i) => {
                    return {
                        n: engine_1.OTActionName.ListDelete,
                        p: ['meta', 'views', index, 'rows', item.index - i],
                        ld: cur.rows[item.index],
                    };
                });
                pre.push(...toDelete);
                return pre;
            }, []);
        }
        recordIds.forEach(recordId => {
            const record = recordMap[recordId];
            if (record) {
                const data = {};
                for (const k in record.data) {
                    const field = getFieldByFieldId(k);
                    if (field_1.Field.bindContext(field, state).recordEditable()) {
                        data[k] = record.data[k];
                    }
                }
                const _record = Object.assign(Object.assign({}, record), { data: data });
                // when delete date, remove alarm
                // TODO(kailang) ObjectDelete has did this
                Object.keys(fieldMap).forEach(fieldId => {
                    if (fieldMap[fieldId].type === field_types_1.FieldType.DateTime) {
                        const alarm = store_1.Selectors.getDateTimeCellAlarm(snapshot, recordId, fieldId);
                        if (alarm) {
                            const alarmActions = DatasheetActions.setDateTimeCellAlarm(snapshot, {
                                recordId,
                                fieldId,
                                alarm: null,
                            });
                            if (alarmActions) {
                                actions.push(...alarmActions);
                            }
                        }
                    }
                });
                actions.push({
                    n: engine_1.OTActionName.ObjectDelete,
                    p: ['recordMap', recordId],
                    od: _record,
                });
            }
        });
        return actions;
    }
    /**
     * set alarm by record
     */
    static setDateTimeCellAlarm(snapshot, payload) {
        var _a, _b, _c;
        const { recordId, fieldId, alarm } = payload;
        const oldAlarm = store_1.Selectors.getDateTimeCellAlarm(snapshot, recordId, fieldId);
        // new alarm
        if (!oldAlarm) {
            const fieldExtraMap = (_b = (_a = snapshot.recordMap[recordId]) === null || _a === void 0 ? void 0 : _a.recordMeta) === null || _b === void 0 ? void 0 : _b.fieldExtraMap;
            // compensate snapshot fieldExtraMap default data
            let defaultAction;
            // without fieldExtraMap
            if (!fieldExtraMap) {
                defaultAction = {
                    n: engine_1.OTActionName.ObjectInsert,
                    p: ['recordMap', recordId, 'recordMeta', 'fieldExtraMap'],
                    oi: {
                        [fieldId]: {},
                    },
                };
            }
            else if (!fieldExtraMap[fieldId]) {
                // already have fieldExtraMap, but still have no fieldExtraMap[fieldId]
                defaultAction = {
                    n: engine_1.OTActionName.ObjectInsert,
                    p: ['recordMap', recordId, 'recordMeta', 'fieldExtraMap', fieldId],
                    oi: {},
                };
            }
            const alarmAction = {
                n: engine_1.OTActionName.ObjectInsert,
                p: ['recordMap', recordId, 'recordMeta', 'fieldExtraMap', fieldId, 'alarm'],
                oi: alarm,
            };
            return defaultAction ? [defaultAction, alarmAction] : [alarmAction];
        }
        if ((0, lodash_1.isEqual)(oldAlarm, alarm)) {
            return null;
        }
        // delete alarm
        if (!alarm) {
            return [
                {
                    n: engine_1.OTActionName.ObjectDelete,
                    p: ['recordMap', recordId, 'recordMeta', 'fieldExtraMap', fieldId, 'alarm'],
                    od: oldAlarm,
                },
            ];
        }
        /**
         * found a situation, alarm.alarmUsers length equals 0,
         * TODO: wait for debug, here just place the check
         */
        if (!((_c = alarm.alarmUsers) === null || _c === void 0 ? void 0 : _c.length)) {
            return null;
        }
        // edit alarm
        return [
            {
                n: engine_1.OTActionName.ObjectReplace,
                p: ['recordMap', recordId, 'recordMeta', 'fieldExtraMap', fieldId, 'alarm'],
                od: oldAlarm,
                oi: alarm,
            },
        ];
    }
    static setViewSort2Action(state, snapshot, payload) {
        const { viewId, sortInfo, applySort } = payload;
        const viewIndex = (0, selectors_1.getViewIndex)(snapshot, viewId);
        if (viewIndex < 0) {
            return null;
        }
        const view = snapshot.meta.views[viewIndex];
        // when clear sorts, delete sort field directly
        if (!sortInfo) {
            return [
                {
                    n: engine_1.OTActionName.ObjectDelete,
                    p: ['meta', 'views', viewIndex, 'sortInfo'],
                    od: view.sortInfo,
                },
            ];
        }
        if (applySort) {
            // sort method will mutate the array, so here duplicate the array first
            const rows = (0, selectors_1.sortRowsBySortInfo)(state, view.rows, sortInfo.rules, snapshot);
            return [
                {
                    n: engine_1.OTActionName.ObjectReplace,
                    p: ['meta', 'views', viewIndex, 'sortInfo'],
                    oi: sortInfo,
                    od: view.sortInfo,
                },
                {
                    n: engine_1.OTActionName.ObjectReplace,
                    p: ['meta', 'views', viewIndex, 'rows'],
                    oi: rows,
                    od: view.rows,
                },
            ];
        }
        return [
            {
                n: engine_1.OTActionName.ObjectReplace,
                p: ['meta', 'views', viewIndex, 'sortInfo'],
                oi: sortInfo,
                od: view.sortInfo,
            },
        ];
    }
    /*
     * generate new viewId
     *
     * @returns {string}
     */
    static getNewViewId(views) {
        return (0, utils_2.getNewId)(utils_2.IDPrefix.View, views.map(view => view.id));
    }
    /**
     * generate new recordId
     * @returns {string}
     * @memberof Table
     */
    static getNewRecordId(recordMap) {
        return (0, utils_2.getNewId)(utils_2.IDPrefix.Record, Object.keys(recordMap));
    }
    /**
     * generate new field id
     * @returns {string}
     * @memberof Table
     */
    static getNewFieldId(fieldMap) {
        return (0, utils_2.getNewId)(utils_2.IDPrefix.Field, fieldMap && Object.keys(fieldMap));
    }
    /**
     * return new record with default value in table
     */
    static getDefaultNewRecord(state, snapshot, recordId, viewId, groupCellValues, userInfo) {
        const fieldMap = (0, selectors_1.getFieldMap)(state, snapshot.datasheetId);
        const record = {
            id: recordId,
            data: {},
            commentCount: 0,
            comments: [],
            recordMeta: {},
        };
        if (!fieldMap) {
            return record;
        }
        for (const fieldId in fieldMap) {
            const field = fieldMap[fieldId];
            const defaultValue = field_1.Field.bindContext(field, state).defaultValue();
            if (defaultValue != null) {
                record.data[field.id] = defaultValue;
            }
        }
        if (!viewId) {
            return record;
        }
        const view = (0, selectors_1.getViewById)(snapshot, viewId);
        if (!view) {
            return record;
        }
        const curMirrorId = state.pageParams.mirrorId;
        let _groupInfo = [];
        let filterInfo;
        if (curMirrorId) {
            _groupInfo = (0, selectors_1.getActiveViewGroupInfo)(state);
            filterInfo = (0, selectors_1.getFilterInfo)(state);
        }
        if (view.groupInfo) {
            const curGroupInfo = (0, selectors_1.getGroupInfoWithPermission)(state, view.groupInfo, snapshot.datasheetId);
            _groupInfo = (0, lodash_1.unionWith)(_groupInfo, curGroupInfo, lodash_1.isEqual);
        }
        if (_groupInfo.length > 0) {
            const dataByGroup = getDefaultNewRecordDataByGroup(_groupInfo, groupCellValues);
            Object.assign(record.data, dataByGroup);
        }
        const curFilterInfo = (0, selectors_1.getFilterInfoExceptInvalid)(state, snapshot.datasheetId, view.filterInfo);
        if (filterInfo && curFilterInfo) {
            filterInfo = {
                conjunction: curFilterInfo.conjunction,
                conditions: curFilterInfo.conditions.concat(filterInfo.conditions),
            };
        }
        else if (curFilterInfo) {
            filterInfo = curFilterInfo;
        }
        if (filterInfo) {
            const dataByFilter = getDefaultNewRecordDataByFilter(state, snapshot.datasheetId, filterInfo, fieldMap, userInfo);
            Object.assign(record.data, dataByFilter);
        }
        return record;
    }
    static getDefaultFieldName(fieldMap) {
        const names = [];
        for (const id in fieldMap) {
            names.push(fieldMap[id].name);
        }
        return (0, utils_2.getUniqName)(utils_2.NamePrefix.Field, names);
    }
    /**
     * return new generated view name
     *
     * @param {ViewType} type
     * @returns {string}
     */
    static getDefaultViewName(views, type) {
        let prefix;
        switch (type) {
            case store_1.ViewType.Grid:
                prefix = utils_2.NamePrefix.GridView;
                break;
            case store_1.ViewType.Gallery:
                prefix = utils_2.NamePrefix.GalleryView;
                break;
            case store_1.ViewType.Kanban:
                prefix = utils_2.NamePrefix.KanbanView;
                break;
            case store_1.ViewType.Form:
                prefix = utils_2.NamePrefix.FormView;
                break;
            case store_1.ViewType.Calendar:
                prefix = utils_2.NamePrefix.CalendarView;
                break;
            case store_1.ViewType.Gantt:
                prefix = utils_2.NamePrefix.GanttView;
                break;
            case store_1.ViewType.OrgChart:
                prefix = utils_2.NamePrefix.OrgChartView;
                break;
            case store_1.ViewType.NotSupport:
                prefix = utils_2.NamePrefix.View;
                break;
            default:
                (0, utils_2.assertNever)(type);
                prefix = utils_2.NamePrefix.View;
        }
        return (0, utils_2.getUniqName)(prefix, views.map(view => view.name));
    }
    /**
     * get default view property with stable row and column order
     */
    static deriveDefaultViewProperty(snapshot, viewType, activeViewId) {
        const defaultProperty = (0, views_1.getViewClass)(viewType).generateDefaultProperty(snapshot, activeViewId);
        if (!defaultProperty) {
            throw Error(`Unexpected view type ${viewType}!`);
        }
        return defaultProperty;
    }
    /**
     * get View's all Records' FieldId data
     * @param {string} fieldId
     * @returns {ICellValue[]}
     * @memberof Table
     */
    static getCellValuesByFieldId(state, snapshot, fieldId, view, isEntity) {
        const fieldMap = snapshot.meta.fieldMap;
        const recordMap = snapshot.recordMap;
        if (!fieldMap[fieldId]) {
            return [];
        }
        const cellValues = [];
        let recordIds = Object.keys(recordMap);
        if (view) {
            switch (view.type) {
                case store_1.ViewType.Grid:
                case store_1.ViewType.Gantt: {
                    recordIds = view.rows.map(view => view.recordId);
                }
            }
        }
        for (let i = 0; i < recordIds.length; i++) {
            const record = recordMap[recordIds[i]];
            const cellValue = (0, selectors_1.getCellValue)(state, snapshot, record.id, fieldId);
            if (isEntity || cellValue != null) {
                cellValues.push(cellValue);
            }
        }
        return cellValues;
    }
    // generate new comment's action
    static insertComment2Action(state, options) {
        const { recordId, datasheetId, insertComments } = options;
        const recordMap = store_1.Selectors.getSnapshot(state, datasheetId).recordMap;
        const record = recordMap[recordId];
        const { comments = [] } = record;
        if (insertComments) {
            return [
                {
                    n: engine_1.OTActionName.ListInsert,
                    p: ['recordMap', recordId, 'comments', comments.length],
                    li: insertComments[0],
                },
                {
                    n: engine_1.OTActionName.NumberAdd,
                    p: ['recordMap', recordId, 'commentCount'],
                    na: 1,
                },
            ];
        }
        return null;
    }
    static updateComment2Action(options) {
        const { recordId, updateComments, emojiAction } = options;
        const actions = [];
        if (emojiAction) {
            // new
            actions.push({
                n: engine_1.OTActionName.ListInsert,
                // p, only verify update comment's emoji, no use in other place
                p: ['recordMap', recordId, 'comments', 'emojis'],
                li: updateComments[0],
            });
        }
        else {
            // cancel
            actions.push({
                n: engine_1.OTActionName.ListDelete,
                // p, only verify update comment's emoji, no use in other place
                p: ['recordMap', recordId, 'comments', 'emojis'],
                ld: updateComments[0],
            });
        }
        return actions;
    }
    static deleteComment2Action(options) {
        const { recordId, comments } = options;
        const actions = [];
        actions.push({
            n: engine_1.OTActionName.ListDelete,
            p: ['recordMap', recordId, 'comments', 0],
            ld: comments[0],
        }, {
            n: engine_1.OTActionName.NumberAdd,
            p: ['recordMap', recordId, 'commentCount'],
            na: -1,
        });
        return actions;
    }
    static deleteWidgetPanel2Action(_state, panelId, widgetPanels, resourceType) {
        if (!widgetPanels) {
            return null;
        }
        const index = widgetPanels.findIndex(item => {
            return item.id === panelId;
        });
        if (index < 0) {
            return null;
        }
        if (resourceType === types_1.ResourceType.Mirror) {
            return [
                {
                    n: engine_1.OTActionName.ListDelete,
                    p: ['widgetPanels', index],
                    ld: widgetPanels[index],
                },
            ];
        }
        return [
            {
                n: engine_1.OTActionName.ListDelete,
                p: ['meta', 'widgetPanels', index],
                ld: widgetPanels[index],
            },
        ];
    }
    static movePanel2Action(targetIndex, sourceIndex, resourceType) {
        if (resourceType === types_1.ResourceType.Mirror) {
            return [
                {
                    n: engine_1.OTActionName.ListMove,
                    p: ['widgetPanels', sourceIndex],
                    lm: targetIndex,
                },
            ];
        }
        return [
            {
                n: engine_1.OTActionName.ListMove,
                p: ['meta', 'widgetPanels', sourceIndex],
                lm: targetIndex,
            },
        ];
    }
    static addWidgetPanel2Action(snapshot, panel) {
        const widgetPanels = snapshot.meta.widgetPanels;
        if (!Array.isArray(widgetPanels)) {
            return [
                {
                    n: engine_1.OTActionName.ObjectInsert,
                    p: ['meta', 'widgetPanels'],
                    oi: [panel],
                },
            ];
        }
        return [
            {
                n: engine_1.OTActionName.ListInsert,
                p: ['meta', 'widgetPanels', widgetPanels.length + 1],
                li: panel,
            },
        ];
    }
    static addWidgetPanelWithMirror2Action(snapshot, panel) {
        const widgetPanels = snapshot.widgetPanels;
        if (!Array.isArray(widgetPanels)) {
            return [
                {
                    n: engine_1.OTActionName.ObjectInsert,
                    p: ['widgetPanels'],
                    oi: [panel],
                },
            ];
        }
        return [
            {
                n: engine_1.OTActionName.ListInsert,
                p: ['widgetPanels', widgetPanels.length + 1],
                li: panel,
            },
        ];
    }
    static modifyPanelName2Acton(_state, newPanel, widgetPanels, resourceType) {
        if (!widgetPanels) {
            return null;
        }
        const index = widgetPanels.findIndex(item => {
            return item.id === newPanel.id;
        });
        if (index < 0) {
            return null;
        }
        if (resourceType === types_1.ResourceType.Mirror) {
            return [
                {
                    n: engine_1.OTActionName.ObjectReplace,
                    p: ['widgetPanels', index, 'name'],
                    od: widgetPanels[index].name,
                    oi: newPanel.name,
                },
            ];
        }
        return [
            {
                n: engine_1.OTActionName.ObjectReplace,
                p: ['meta', 'widgetPanels', index, 'name'],
                od: widgetPanels[index].name,
                oi: newPanel.name,
            },
        ];
    }
    static addWidgetToPanel2Action(_state, { installationIndex, panelIndex, widgetId }) {
        const newWidget = {
            id: widgetId,
            height: 6.2,
            y: Number.MAX_SAFE_INTEGER,
        };
        return [
            {
                n: engine_1.OTActionName.ListInsert,
                p: ['meta', 'widgetPanels', panelIndex, 'widgets', installationIndex],
                li: newWidget,
            },
        ];
    }
    static addWidgetToPanelWithMirror2Action(_state, { installationIndex, panelIndex, widgetId }) {
        const newWidget = {
            id: widgetId,
            height: 6.2,
            y: Number.MAX_SAFE_INTEGER,
        };
        return [
            {
                n: engine_1.OTActionName.ListInsert,
                p: ['widgetPanels', panelIndex, 'widgets', installationIndex],
                li: newWidget,
            },
        ];
    }
    static deleteWidget2Action(_state, options) {
        const { widgetPanelIndex, widgetIndex, widget } = options;
        return [
            {
                n: engine_1.OTActionName.ListDelete,
                p: ['meta', 'widgetPanels', widgetPanelIndex, 'widgets', widgetIndex],
                ld: widget,
            },
        ];
    }
    static deleteMirrorWidget2Action(_state, options) {
        const { widgetPanelIndex, widgetIndex, widget } = options;
        return [
            {
                n: engine_1.OTActionName.ListDelete,
                p: ['widgetPanels', widgetPanelIndex, 'widgets', widgetIndex],
                ld: widget,
            },
        ];
    }
    static changeWidgetHeight2Action(state, { widgetPanelIndex, widgetIndex, widgetHeight, resourceId, resourceType, }) {
        const activeWidgetPanel = (0, selectors_1.getResourceActiveWidgetPanel)(state, resourceId, resourceType);
        const widget = activeWidgetPanel.widgets[widgetIndex];
        const path = resourceType === types_1.ResourceType.Datasheet
            ? ['meta', 'widgetPanels', widgetPanelIndex, 'widgets', widgetIndex, 'height']
            : ['widgetPanels', widgetPanelIndex, 'widgets', widgetIndex, 'height'];
        return [
            {
                n: engine_1.OTActionName.ObjectReplace,
                p: path,
                oi: widgetHeight,
                od: widget.height,
            },
        ];
    }
    static moveWidget2Action(state, { widgetPanelIndex, layout, resourceType, resourceId, }) {
        var _a;
        const widgetPanel = (0, selectors_1.getResourceWidgetPanels)(state, resourceId, resourceType);
        const oldLayout = (_a = widgetPanel === null || widgetPanel === void 0 ? void 0 : widgetPanel[widgetPanelIndex]) === null || _a === void 0 ? void 0 : _a.widgets;
        if (!oldLayout || layout.length !== oldLayout.length) {
            return null;
        }
        const actions = [];
        const getPath = (widgetPanelIndex, index, key) => {
            const basePath = ['widgetPanels', widgetPanelIndex, 'widgets', index, key];
            return resourceType === types_1.ResourceType.Mirror ? basePath : ['meta', ...basePath];
        };
        oldLayout.forEach((oldPosition, index) => {
            const newPosition = layout[index];
            for (const k in newPosition) {
                const oldValue = oldPosition[k];
                const newValue = newPosition[k];
                if (oldValue !== newValue) {
                    // compatible with the old mini program panel, set the y coordinate attribute for the first time, use oi
                    const isFirstSetY = k === 'y' && oldValue === undefined;
                    const replaceAction = {
                        n: engine_1.OTActionName.ObjectReplace,
                        p: getPath(widgetPanelIndex, index, k),
                        oi: newValue,
                        od: oldValue,
                    };
                    const insertAction = {
                        n: engine_1.OTActionName.ObjectInsert,
                        p: getPath(widgetPanelIndex, index, k),
                        oi: newValue,
                    };
                    actions.push(isFirstSetY ? insertAction : replaceAction);
                }
            }
        });
        return actions;
    }
    static manualSaveView2Action(snapshot, payload) {
        const { viewId, viewProperty: serverView } = payload;
        const viewIndex = snapshot.meta.views.findIndex(view => view.id === viewId);
        if (viewIndex == null) {
            return null;
        }
        const localView = snapshot.meta.views[viewIndex];
        const action = [];
        const integrationView = Object.assign(Object.assign({}, (0, lodash_1.omit)(serverView, engine_1.ViewPropertyFilter.ignoreViewProperty)), (0, lodash_1.omit)(localView, engine_1.ViewPropertyFilter.ignoreViewProperty));
        for (const key in integrationView) {
            if (localView[key] === serverView[key]) {
                continue;
            }
            if (localView.hasOwnProperty(key) && serverView.hasOwnProperty(key)) {
                action.push({
                    n: engine_1.OTActionName.ObjectReplace,
                    p: ['meta', 'views', viewIndex, key],
                    oi: localView[key],
                    od: serverView[key],
                });
            }
            if (localView.hasOwnProperty(key) && !serverView.hasOwnProperty(key)) {
                action.push({
                    n: engine_1.OTActionName.ObjectInsert,
                    p: ['meta', 'views', viewIndex, key],
                    oi: localView[key],
                });
            }
            if (!localView.hasOwnProperty(key) && serverView.hasOwnProperty(key)) {
                action.push({
                    n: engine_1.OTActionName.ObjectDelete,
                    p: ['meta', 'views', viewIndex, key],
                    od: serverView[key],
                });
            }
        }
        return action;
    }
    static resetView2Action(snapshot, payload) {
        const { viewId, viewProperty: serverView } = payload;
        const viewIndex = snapshot.meta.views.findIndex(view => view.id === viewId);
        if (viewIndex == null) {
            return null;
        }
        const localView = snapshot.meta.views[viewIndex];
        const action = [];
        const integrationView = Object.assign(Object.assign({}, (0, lodash_1.omit)(localView, engine_1.ViewPropertyFilter.ignoreViewProperty)), (0, lodash_1.omit)(serverView, engine_1.ViewPropertyFilter.ignoreViewProperty));
        for (const key in integrationView) {
            if (localView[key] === serverView[key]) {
                continue;
            }
            if (localView[key] && serverView[key]) {
                action.push({
                    n: engine_1.OTActionName.ObjectReplace,
                    p: ['meta', 'views', viewIndex, key],
                    oi: serverView[key],
                    od: localView[key],
                });
            }
            if (localView[key] && !serverView[key]) {
                action.push({
                    n: engine_1.OTActionName.ObjectDelete,
                    p: ['meta', 'views', viewIndex, key],
                    od: localView[key],
                });
            }
            if (!localView[key] && serverView[key]) {
                action.push({
                    n: engine_1.OTActionName.ObjectInsert,
                    p: ['meta', 'views', viewIndex, key],
                    oi: serverView[key],
                });
            }
        }
        return action;
    }
    static setViewAutoSave2Action(snapshot, { viewId, autoSave }) {
        const views = snapshot.meta.views;
        const viewIndex = views.findIndex(view => view.id === viewId);
        if (viewIndex == null) {
            return null;
        }
        const view = views[viewIndex];
        if (view.autoSave == null) {
            return {
                n: engine_1.OTActionName.ObjectInsert,
                p: ['meta', 'views', viewIndex, 'autoSave'],
                oi: autoSave,
            };
        }
        return {
            n: engine_1.OTActionName.ObjectReplace,
            p: ['meta', 'views', viewIndex, 'autoSave'],
            oi: autoSave,
            od: view['autoSave'],
        };
    }
    /**
     * edit single relation dstID
     *
     * @param snapshot
     * @param payload
     */
    static changeOneWayLinkDstId2Action(snapshot, payload) {
        const fieldMap = snapshot.meta.fieldMap;
        const { fieldId, newField } = payload;
        if (!fieldMap[fieldId]) {
            return null;
        }
        return {
            n: engine_1.OTActionName.ObjectReplace,
            p: ['meta', 'fieldMap', fieldId],
            od: fieldMap[fieldId],
            oi: newField,
        };
    }
}
exports.DatasheetActions = DatasheetActions;
/**
 * move views
 * @param {string} viewId
 */
DatasheetActions.moveView2Action = (snapshot, payload) => {
    const { viewId, target } = payload;
    const views = snapshot.meta.views;
    let index = -1;
    for (let i = 0, l = views.length; i < l; i++) {
        if (views[i].id === viewId) {
            index = i;
            break;
        }
    }
    if (index === -1) {
        return null;
    }
    // moveArrayElement(views, index, target);
    return {
        n: engine_1.OTActionName.ListMove,
        p: ['meta', 'views', index],
        lm: target,
    };
};
DatasheetActions.setColumnWidth2Action = (snapshot, payload) => {
    const { viewId, fieldId, width } = payload;
    const viewIndex = (0, selectors_1.getViewIndex)(snapshot, viewId);
    if (viewIndex < 0) {
        return null;
    }
    const view = snapshot.meta.views[viewIndex];
    const columnIndex = view.columns.findIndex(column => column.fieldId === fieldId);
    const column = view.columns[columnIndex];
    if (columnIndex < 0 || ![store_1.ViewType.Grid, store_1.ViewType.Gantt].includes(view.type) || column.width === width) {
        return null;
    }
    return {
        n: engine_1.OTActionName.ObjectReplace,
        p: ['meta', 'views', viewIndex, 'columns', columnIndex, 'width'],
        oi: width,
        od: column.width,
    };
};
DatasheetActions.moveColumns2Action = (snapshot, payload) => {
    const { fieldId, target, viewId } = payload;
    const viewIndex = (0, selectors_1.getViewIndex)(snapshot, viewId);
    if (viewIndex < 0) {
        return null;
    }
    const view = snapshot.meta.views[viewIndex];
    const columnIndex = view.columns.findIndex(column => column.fieldId === fieldId);
    if (columnIndex < 0 || columnIndex === target) {
        return null;
    }
    return {
        n: engine_1.OTActionName.ListMove,
        p: ['meta', 'views', viewIndex, 'columns', columnIndex],
        lm: target,
    };
};
/**
 * set grid view's column's statistic dimension
 */
DatasheetActions.setColumnStatType2Action = (snapshot, payload) => {
    const { fieldId, statType, viewId } = payload;
    const viewIndex = (0, selectors_1.getViewIndex)(snapshot, viewId);
    if (viewIndex < 0) {
        return null;
    }
    const view = snapshot.meta.views[viewIndex];
    const columnIndex = view.columns.findIndex(column => column.fieldId === fieldId);
    const column = view.columns[columnIndex];
    if (columnIndex < 0 || ![store_1.ViewType.Grid, store_1.ViewType.Gantt].includes(view.type) || column.statType === statType) {
        return null;
    }
    if (!statType) {
        return {
            n: engine_1.OTActionName.ObjectDelete,
            p: ['meta', 'views', viewIndex, 'columns', columnIndex, 'statType'],
            od: column.statType,
        };
    }
    return {
        n: engine_1.OTActionName.ObjectReplace,
        p: ['meta', 'views', viewIndex, 'columns', columnIndex, 'statType'],
        oi: statType,
        od: column.statType,
    };
};
/**
 * set the row height of gridview View
 */
DatasheetActions.setRowHeightLevel2Action = (snapshot, payload) => {
    const { level, viewId } = payload;
    const viewIndex = (0, selectors_1.getViewIndex)(snapshot, viewId);
    if (viewIndex < 0) {
        return null;
    }
    const view = snapshot.meta.views[viewIndex];
    if (![store_1.ViewType.Grid, store_1.ViewType.Gantt].includes(view.type) || view.rowHeightLevel === level) {
        return null;
    }
    return {
        n: engine_1.OTActionName.ObjectReplace,
        p: ['meta', 'views', viewIndex, 'rowHeightLevel'],
        oi: level,
        od: view.rowHeightLevel,
    };
};
/**
 * set Grid/Gantt view's column whether auto word wrap
 */
DatasheetActions.setAutoHeadHeight2Action = (snapshot, payload) => {
    const { isAuto, viewId } = payload;
    const viewIndex = (0, selectors_1.getViewIndex)(snapshot, viewId);
    if (viewIndex < 0) {
        return null;
    }
    const view = snapshot.meta.views[viewIndex];
    if (![store_1.ViewType.Grid, store_1.ViewType.Gantt].includes(view.type) || view.autoHeadHeight === isAuto) {
        return null;
    }
    return {
        n: engine_1.OTActionName.ObjectReplace,
        p: ['meta', 'views', viewIndex, 'autoHeadHeight'],
        oi: isAuto,
        od: view.autoHeadHeight,
    };
};
DatasheetActions.setFrozenColumnCount2Action = (snapshot, payload) => {
    const { count, viewId } = payload;
    const viewIndex = (0, selectors_1.getViewIndex)(snapshot, viewId);
    if (viewIndex < 0) {
        return null;
    }
    const view = snapshot.meta.views[viewIndex];
    if (![store_1.ViewType.Grid, store_1.ViewType.Gantt].includes(view.type) || view.frozenColumnCount === count) {
        return null;
    }
    return {
        n: engine_1.OTActionName.ObjectReplace,
        p: ['meta', 'views', viewIndex, 'frozenColumnCount'],
        oi: count,
        od: view.frozenColumnCount,
    };
};
DatasheetActions.moveRow2Action = (snapshot, payload) => {
    const { recordId, target, viewId } = payload;
    const viewIndex = (0, selectors_1.getViewIndex)(snapshot, viewId);
    if (viewIndex < 0) {
        return null;
    }
    const view = snapshot.meta.views[viewIndex];
    const recordIndex = view.rows.findIndex(row => row.recordId === recordId);
    if (recordIndex < 0) {
        return null;
    }
    return {
        n: engine_1.OTActionName.ListMove,
        p: ['meta', 'views', viewIndex, 'rows', recordIndex],
        lm: target,
    };
};
/**
 * update Field, replace field directly
 */
DatasheetActions.setFieldAttr2Action = (snapshot, payload) => {
    const fieldMap = snapshot.meta.fieldMap;
    const field = payload.field;
    if (!fieldMap[field.id] || (0, lodash_1.isEqual)(field, fieldMap[field.id])) {
        return null;
    }
    return {
        n: engine_1.OTActionName.ObjectReplace,
        p: ['meta', 'fieldMap', field.id],
        oi: field,
        od: fieldMap[field.id],
    };
};
/**
 * set view filter  filterInfo
 */
DatasheetActions.setFilterInfo2Action = (snapshot, payload) => {
    const viewId = payload.viewId;
    let filterInfo = payload.filterInfo;
    const viewIndex = (0, selectors_1.getViewIndex)(snapshot, viewId);
    if (viewIndex < 0) {
        return null;
    }
    const view = snapshot.meta.views[viewIndex];
    if (!validateFilterInfo(filterInfo)) {
        console.error('illegal filter condition!');
        filterInfo = undefined;
    }
    if ((0, lodash_1.isEqual)(view.filterInfo, filterInfo)) {
        return null;
    }
    /**
     * when clear filter, delete filter field directly
     */
    if (!filterInfo) {
        return {
            n: engine_1.OTActionName.ObjectDelete,
            p: ['meta', 'views', viewIndex, 'filterInfo'],
            od: view.filterInfo,
        };
    }
    return {
        n: engine_1.OTActionName.ObjectReplace,
        p: ['meta', 'views', viewIndex, 'filterInfo'],
        oi: filterInfo,
        od: view.filterInfo,
    };
};
DatasheetActions.setViewLockInfo2Action = (snapshot, payload) => {
    const viewId = payload.viewId;
    const viewLock = payload.viewLockInfo;
    const viewIndex = (0, selectors_1.getViewIndex)(snapshot, viewId);
    if (viewIndex < 0) {
        return null;
    }
    const view = snapshot.meta.views[viewIndex];
    if (viewLock === null) {
        return {
            n: engine_1.OTActionName.ObjectDelete,
            p: ['meta', 'views', viewIndex, 'lockInfo'],
            od: view.lockInfo,
        };
    }
    return {
        n: engine_1.OTActionName.ObjectInsert,
        p: ['meta', 'views', viewIndex, 'lockInfo'],
        oi: viewLock,
    };
};
DatasheetActions.setGroupInfoField2Action = (snapshot, payload) => {
    const { viewId, groupInfo } = payload;
    const viewIndex = (0, selectors_1.getViewIndex)(snapshot, viewId);
    if (viewIndex < 0) {
        return null;
    }
    const view = snapshot.meta.views[viewIndex];
    if ((0, lodash_1.isEqual)(view.groupInfo, groupInfo)) {
        return null;
    }
    compensator_1.compensator.setLastGroupInfoIfNull(view.groupInfo);
    /**
     * when clear grouping, delete grouping field directly
     */
    if (!groupInfo) {
        return {
            n: engine_1.OTActionName.ObjectDelete,
            p: ['meta', 'views', viewIndex, 'groupInfo'],
            od: view.groupInfo,
        };
    }
    return {
        n: engine_1.OTActionName.ObjectReplace,
        p: ['meta', 'views', viewIndex, 'groupInfo'],
        oi: groupInfo,
        od: view.groupInfo,
    };
};
//# sourceMappingURL=datasheet.js.map